import React, { useContext } from "react";
import classnames from "classnames";
import dynamic from "next/dynamic";

import { Action, Category } from "@/constants/events";
import StoreLocatorContext from "@/context/StoreLocatorContext";
import { MapMarkerDisplayTypeEnum, Store } from "@/custom-types/StoreLocator";
import { useEventTracker } from "@/hooks/useEventTracker";
import { getOrganicCardEventLabel } from "@/utils/storeLocator/eventTracking";

import ScheduleStatus from "@/components/botanic/ScheduleStatus";
import StarRating from "@/components/botanic/StarRating";
import PickupLabel from "@/components/dispensaries/StoreCard/PickupLabel";
import Image from "@/components/Image";

const AwardBadgeIcon = dynamic(
  () => import("@/components/Icons/award_badge.svg"),
);

const ScooterIcon = dynamic(() => import("@/components/Icons/scooter.svg"));

const StorefrontIcon = dynamic(
  () => import("@/components/Icons/storefront.svg"),
);

const MobileStoreCard: React.FC<{
  store: Store;
}> = ({ store }) => {
  const { publishEvent } = useEventTracker();

  const { selectedMarker } = useContext(StoreLocatorContext);

  const eventLabel =
    selectedMarker?.displayType ===
    MapMarkerDisplayTypeEnum.ProminentImageContainer
      ? "platinum placement"
      : getOrganicCardEventLabel(store.featureTier);

  const onClick = () => {
    publishEvent({
      action: Action.click,
      category: Category.finder,
      dealId: store.dealId || undefined,
      dispensaryId: store.id,
      label: eventLabel,
      merchandisingCampaignId: store.merchandisingCampaignId || undefined,
    });
  };

  return (
    <div
      className="bg-white shadow-low flex rounded m-4 p-md relative overflow-hidden"
      data-testid="mobile-overlay-store-card"
    >
      <a
        data-testid="mobile-card-link"
        className="w-full flex flex-wrap justify-between relative"
        href={store.path}
        onClick={onClick}
      >
        {selectedMarker?.displayType ===
          MapMarkerDisplayTypeEnum.ProminentImageContainer && (
          <div className="top-0 right-0 absolute">
            <span
              className={classnames(
                "text-xs bg-leafly-white rounded p-1 mt-1 self-end text-grey",
              )}
            >
              ad
            </span>
          </div>
        )}
        <div className="mx-auto h-[60px] w-[60px]">
          {store.logoUrl ? (
            <Image
              src={store.logoUrl}
              ratio="square"
              sizes={[60]}
              alt={`${store.name}-logo`}
              disableLazyLoad={true}
            />
          ) : (
            <div className="p-md">
              <StorefrontIcon
                width="100%"
                height="100%"
                className="text-grey"
                aria-label={`Default logo for ${store.name}`}
              />
            </div>
          )}
        </div>
        <div className="flex flex-wrap w-3/4">
          <div className="grid w-5/6">
            <div className="flex justify-between">
              <div className="text-xs underline truncate font-bold">
                {store.configurations.leaflyListEnabled && (
                  <AwardBadgeIcon
                    width="24"
                    height="24"
                    className="flex-shrink-0 inline pr-1"
                  />
                )}
                <span>{store.name}</span>
              </div>
            </div>
            {store.reviewCount > 0 && (
              <StarRating
                rating={store.reviewRating}
                ratingCount={store.reviewCount}
                starWidth={13}
              />
            )}
          </div>
          {(store.configurations.deliveryEnabled ||
            store.configurations.pickupEnabled) && (
            <div className="flex flex-wrap my-xs">
              {store.configurations.deliveryEnabled && (
                <div className="flex items-center bg-leafly-white rounded px-sm mr-sm">
                  <ScooterIcon width="12" height="12" aria-hidden="true" />
                  <span className="ml-xs text-xs font-bold my-0.5">
                    Delivery
                  </span>
                </div>
              )}
              {store.configurations.pickupEnabled && (
                <PickupLabel
                  pickupReadiness={store.configurations.pickupReadinessEnabled}
                />
              )}
            </div>
          )}
          <div className="flex flex-row items-center my-xs text-xs">
            <span className="overflow-hidden">
              {store.openStatus && (
                <ScheduleStatus
                  currentStatus={store.openStatus}
                  className="min-h-5 flex-shrink-0 font-bold"
                  preorderConfigured={store.configurations.preorderEnabled}
                  scheduleType={"store"}
                  timeZone={store.timeZone}
                />
              )}
            </span>
            {store.openStatus && store.fulfillmentDetails && (
              <span className="mx-sm flex-shrink-0 leading-none after:content-['•']"></span>
            )}
            {store.fulfillmentDetails && (
              <span className="flex-shrink-0">{store.fulfillmentDetails}</span>
            )}
          </div>
        </div>
      </a>
    </div>
  );
};

export default MobileStoreCard;
